/**
 * "When should it alert?" — from the event's own values, as a sentence the operator completes:
 *
 *   ( ) Every time the event is seen
 *   (•) Only when the event's values match
 *         [ temperature    ] [is greater than ▾] [ 60     ]  ✕
 *         [ read_text      ] [contains        ▾] [ PALLET ]  ✕
 *         + Add another condition          Alert when [all ▾] of these match
 *   [ ] Also check how sure the AI is      at least [60%]
 *
 * The value name is typed (suggestions: what the AI has actually sent for the rule's events, plus
 * `read_text` — everything an OCR event read); the limit is the rule's.
 *
 * Stored as conditions { match, rules: [{ field, op, value }] } and min_confidence (0 = not
 * checked) — ruleConditionsModel; evaluated by the command-center's ruleConditions. No timing
 * here: a rule alerts on an event's values. A rule saved with a wait (sustain_seconds) shows it,
 * with Remove.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  BUILTIN_FIELDS,
  FIELD_LABEL,
  OP_WORDS,
  OP_WORD,
  coerceValue,
  fieldsFor,
  formatSeconds,
  opKind,
  parseConditions,
  type CatalogField,
  type EventCatalogEntry,
  type RuleCondition,
  type RuleConditions,
} from './ruleConditionsModel';

export interface RuleWhenProps {
  form: Record<string, any>;
  set: (patch: Record<string, any>) => void;
  catalog: EventCatalogEntry[];
  /** The event names the rule matches (main and sub) — which value names to suggest. */
  names: string[];
}

const inCls = 'rounded-lg border border-border bg-background px-2.5 py-1.5 text-[13px] text-foreground';

/** "the AI has sent 18 – 74" / "the AI has sent: moving, stopped" — what a value name holds. */
function seenText(f?: CatalogField): string {
  if (!f) return '';
  if (f.type === 'number' && f.min !== undefined && Number.isFinite(f.min)) return f.min === f.max ? `sent: ${f.min}` : `sent: ${f.min} to ${f.max}`;
  if (f.samples?.length) return `sent: ${f.samples.slice(0, 3).join(', ')}`;
  return '';
}

export function sureText(pct: number) {
  if (pct < 40) return 'Very sensitive — catches more, but expect false alarms';
  if (pct < 70) return 'Balanced — the usual choice';
  return 'Strict — only when the AI is sure, may miss some';
}

const EXAMPLES: { label: string; rule: RuleCondition }[] = [
  { label: 'temperature is greater than 60', rule: { field: 'temperature', op: 'gt', value: 60 } },
  { label: 'the text read contains PALLET', rule: { field: 'read_text', op: 'contains', value: 'PALLET' } },
  { label: 'state is equal to no_belt_movement', rule: { field: 'state', op: 'eq', value: 'no_belt_movement' } },
];

/** One condition: [value name] [test] [value]. Keeps what is typed locally so "1," or "-" can be typed mid-edit. */
function ConditionRow({ rule, fields, listId, first, onChange, onRemove }: {
  rule: RuleCondition; fields: Record<string, CatalogField>; listId: string; first: boolean;
  onChange: (r: RuleCondition) => void; onRemove: () => void;
}) {
  const field = fields[rule.field];
  const kind = opKind(rule.op);
  const toText = (v: unknown) => (Array.isArray(v) ? v.join(', ') : v === undefined || v === null ? '' : String(v));
  const [text, setText] = useState(() => toText(rule.value));
  const [low, setLow] = useState(() => (Array.isArray(rule.value) ? String(rule.value[0] ?? '') : ''));
  const [high, setHigh] = useState(() => (Array.isArray(rule.value) ? String(rule.value[1] ?? '') : ''));
  useEffect(() => {
    setText(toText(rule.value));
    setLow(Array.isArray(rule.value) ? String(rule.value[0] ?? '') : '');
    setHigh(Array.isArray(rule.value) ? String(rule.value[1] ?? '') : '');
  }, [rule.op, rule.field]); // eslint-disable-line react-hooks/exhaustive-deps
  const samplesId = useMemo(() => `when-samples-${Math.random().toString(36).slice(2, 8)}`, []);
  const setOp = (op: string) => {
    const k = opKind(op);
    onChange({ ...rule, op: op as RuleCondition['op'], value: k === 'range' ? coerceValue(op, [low, high]) : coerceValue(op, text) });
  };
  const known = [...OP_WORDS.number, ...OP_WORDS.text].some((o) => o.value === rule.op);

  return (
    <div className="grid grid-cols-[2.5rem_minmax(0,9.5rem)_minmax(0,9.5rem)_minmax(8rem,1fr)_1.75rem] items-start gap-2">
      <span className="pt-2 text-right text-[12px] font-medium text-muted-foreground">{first ? 'When' : 'and'}</span>
      <div>
        <input
          list={listId}
          value={rule.field}
          onChange={(e) => onChange({ ...rule, field: e.target.value.trim() })}
          placeholder="value name"
          className={`${inCls} w-full font-mono`}
          aria-label="Value name"
        />
        <p className="mt-0.5 truncate text-[11px] text-muted-foreground" title={FIELD_LABEL[rule.field] || seenText(field)}>
          {FIELD_LABEL[rule.field] || seenText(field) || ' '}
        </p>
      </div>
      <select value={rule.op} onChange={(e) => setOp(e.target.value)} className={`${inCls} w-full`} aria-label="Test">
        {!known && <option value={rule.op}>{OP_WORD[rule.op] || rule.op}</option>}
        <optgroup label="Numbers">
          {OP_WORDS.number.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </optgroup>
        <optgroup label="Text or numbers">
          {OP_WORDS.text.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </optgroup>
      </select>
      <div>
        {kind === 'range' ? (
          <div className="flex items-center gap-1">
            <input value={low} inputMode="decimal" placeholder="from" className={`${inCls} w-full`} aria-label="From"
              onChange={(e) => { setLow(e.target.value); onChange({ ...rule, value: coerceValue(rule.op, [e.target.value, high]) }); }} />
            <span className="text-[12px] text-muted-foreground">and</span>
            <input value={high} inputMode="decimal" placeholder="to" className={`${inCls} w-full`} aria-label="To"
              onChange={(e) => { setHigh(e.target.value); onChange({ ...rule, value: coerceValue(rule.op, [low, e.target.value]) }); }} />
          </div>
        ) : (
          <input
            value={text}
            list={field?.samples?.length ? samplesId : undefined}
            inputMode={kind === 'number' ? 'decimal' : undefined}
            placeholder={rule.op === 'matches' ? 'e.g. ^GJ\\d{2}' : kind === 'list' ? 'a, b, c' : kind === 'number' ? 'e.g. 150' : 'e.g. PALLET'}
            onChange={(e) => { setText(e.target.value); onChange({ ...rule, value: coerceValue(rule.op, e.target.value) }); }}
            className={`${inCls} w-full ${rule.op === 'matches' ? 'font-mono' : ''}`}
            aria-label="Value"
          />
        )}
        {field?.samples?.length ? <datalist id={samplesId}>{field.samples.map((x) => <option key={x} value={x} />)}</datalist> : null}
        <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
          {rule.op === 'matches' ? '^ starts with · \\d a digit · (A|B) A or B' : kind === 'list' ? 'separate with commas' : ' '}
        </p>
      </div>
      <button type="button" onClick={onRemove} className="mt-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive" aria-label="Remove this condition">
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

/** "temperature is greater than 60 and read_text contains PALLET" — for the sentence on top. */
export function whenSentence(form: Record<string, any>): string {
  const c = parseConditions(form.conditions);
  // Only finished conditions: a name, and a value (unless the test needs none).
  const done = (r: RuleCondition) => !!r.field && (opKind(r.op) === 'none'
    || (Array.isArray(r.value) ? r.value.length > 0 && r.value.every((v) => v !== '') : r.value !== undefined && r.value !== null && r.value !== ''));
  const parts = (c?.rules || []).filter(done).map((r) => {
    const v = Array.isArray(r.value) ? (opKind(r.op) === 'range' ? `${r.value[0]} and ${r.value[1]}` : r.value.join(', ')) : r.value ?? '';
    return `${r.field} ${OP_WORD[r.op] || r.op} ${v}`.trim();
  });
  return parts.join(c?.match === 'any' ? ' or ' : ' and ');
}

export default function RuleWhen({ form, set, catalog, names }: RuleWhenProps) {
  const c: RuleConditions = parseConditions(form.conditions) || { match: 'all', rules: [] };
  const fields: Record<string, CatalogField> = useMemo(
    () => ({ ...fieldsFor(catalog || [], names), read_text: BUILTIN_FIELDS.read_text }),
    [catalog, names.join(',')], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const listId = useMemo(() => `when-fields-${Math.random().toString(36).slice(2, 8)}`, []);
  const [byValue, setByValue] = useState(c.rules.length > 0);
  const pct = Math.round((parseFloat(form.min_confidence) || 0) * 100);
  const hold = Number(form.sustain_seconds) || 0;
  const setRules = (rules: RuleCondition[]) => set({ conditions: rules.length ? { ...c, rules } : null });
  // A stable key per row: removing row 1 must not hand row 2 row 1's typed value.
  const rowIds = useRef<number[]>([]);
  const nextRowId = useRef(0);
  while (rowIds.current.length < c.rules.length) rowIds.current.push(nextRowId.current++);
  if (rowIds.current.length > c.rules.length) rowIds.current.length = c.rules.length;
  // The command-center's OCR event names (eventClassifier) — read every frame, so they only alert
  // by rule with a condition on what was read (alertEvaluator isOcrRule).
  const isOcr = names.some((n) => ['ocr', 'ocr_detection', 'text_detection'].includes(n.trim().toLowerCase().replace(/[\s.-]+/g, '_')));
  const suggestions = Object.keys(fields);

  const choose = (v: boolean) => {
    setByValue(v);
    if (!v) set({ conditions: null });
    else if (!c.rules.length) setRules([{ field: '', op: 'gt', value: '' }]);
  };
  const radio = (on: boolean, title: string, hint: string, onClick: () => void) => (
    <button type="button" onClick={onClick} role="radio" aria-checked={on}
      className={`flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${on ? 'border-brand bg-brand-light' : 'border-border bg-card hover:bg-muted'}`}>
      <span className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 ${on ? 'border-brand' : 'border-muted-foreground'}`}>
        {on && <span className="h-2 w-2 rounded-full bg-brand" />}
      </span>
      <span>
        <span className="block text-[14px] font-semibold text-foreground">{title}</span>
        <span className="block text-[12px] text-muted-foreground">{hint}</span>
      </span>
    </button>
  );
  const PRESETS = [{ label: 'Loose', pct: 30 }, { label: 'Balanced', pct: 50 }, { label: 'Strict', pct: 80 }];

  return (
    <div className="space-y-3">
      <datalist id={listId}>
        {suggestions.map((name) => <option key={name} value={name}>{FIELD_LABEL[name] || seenText(fields[name]) || name}</option>)}
      </datalist>

      <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" role="radiogroup" aria-label="When it alerts">
        {radio(!byValue, 'Every time the event is seen', 'No conditions.', () => choose(false))}
        {radio(byValue, "Only when the event's values match", 'e.g. temperature is greater than 60.', () => choose(true))}
      </div>

      {byValue && (
        <div className="space-y-2 rounded-xl border border-border px-3 py-3">
          <p className="text-[12px] text-muted-foreground">
            Type the <b className="text-foreground">name of a value</b> the AI sends with the event{suggestions.length > 1 ? ' (or pick one it has sent)' : ''}, choose the test, and enter what to compare it with.
          </p>
          {c.rules.map((r, i) => (
            <ConditionRow
              key={rowIds.current[i]}
              rule={r}
              first={i === 0}
              fields={fields}
              listId={listId}
              onChange={(next) => setRules(c.rules.map((x, j) => (j === i ? next : x)))}
              onRemove={() => {
                rowIds.current.splice(i, 1);
                const rest = c.rules.filter((_, j) => j !== i);
                setRules(rest);
                if (!rest.length) setByValue(false);
              }}
            />
          ))}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pl-[3rem]">
            <button type="button" onClick={() => setRules([...c.rules, { field: '', op: 'gt', value: '' }])}
              className="inline-flex items-center gap-1 text-[12px] font-medium text-brand hover:underline">
              <PlusIcon className="h-4 w-4" /> Add another condition
            </button>
            {c.rules.length > 1 && (
              <label className="flex items-center gap-1.5 text-[12px] text-foreground">
                Alert when
                <select value={c.match} onChange={(e) => set({ conditions: { ...c, match: e.target.value } })} className={`${inCls} py-1`} aria-label="All or any">
                  <option value="all">all of them</option>
                  <option value="any">any one</option>
                </select>
                match
              </label>
            )}
          </div>
          {c.rules.length === 1 && !c.rules[0].field && (
            <div className="flex flex-wrap items-center gap-1.5 pl-[3rem] text-[12px] text-muted-foreground">
              Examples:
              {EXAMPLES.map((x) => (
                <button key={x.label} type="button" onClick={() => setRules([x.rule])} className="rounded-full border border-border px-2 py-0.5 text-foreground hover:bg-muted">
                  {x.label}
                </button>
              ))}
            </div>
          )}
          {isOcr && <p className="pl-[3rem] text-[11px] text-muted-foreground">OCR reads every frame: these alerts repeat at most every 30 seconds per camera.</p>}
        </div>
      )}
      {isOcr && !byValue && (
        <p className="rounded-lg border border-amber-500 px-2.5 py-1.5 text-[12px] text-foreground">
          An OCR rule <b>needs a condition on what was read</b> (e.g. read_text contains a word) — a camera reads every frame, so “every time” never alerts.
        </p>
      )}

      {hold > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500 px-2.5 py-1.5 text-[12px] text-foreground">
          <span className="min-w-0 flex-1">This rule also waits until the values have held for <b>{formatSeconds(hold)}</b> (an older setting).</span>
          <button type="button" onClick={() => set({ sustain_seconds: 0 })} className="rounded-lg border border-border px-2 py-0.5 font-medium hover:bg-muted">Remove the wait</button>
        </div>
      )}

      <div className="space-y-3 rounded-xl bg-muted px-4 py-3.5">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
            <input type="checkbox" checked={pct > 0} onChange={(e) => set({ min_confidence: e.target.checked ? 0.5 : 0 })} className="h-[18px] w-[18px] accent-brand" />
            <span className="text-[14px] font-semibold text-foreground">Minimum AI confidence</span>
          </label>
          {pct > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((x) => (
                <button key={x.label} type="button" onClick={() => set({ min_confidence: x.pct / 100 })} aria-pressed={pct === x.pct}
                  className={`rounded-lg border px-3 py-1 text-[13px] ${pct === x.pct ? 'border-brand bg-card font-medium text-foreground' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
                  {x.label} · {x.pct}%
                </button>
              ))}
            </div>
          )}
        </div>
        {pct > 0 ? (
          <>
            <div className="flex items-center gap-4">
              <input type="range" min={5} max={100} step={5} value={pct}
                onChange={(e) => set({ min_confidence: (parseInt(e.target.value, 10) || 5) / 100 })}
                className="min-w-0 flex-1 accent-brand" aria-label="How sure the AI must be" />
              <span className="w-12 text-right text-[16px] font-semibold text-foreground">{pct}%</span>
            </div>
            <p className="text-[12px] text-muted-foreground">{sureText(pct)}</p>
          </>
        ) : (
          <p className="text-[12px] text-muted-foreground">Off — every matching event counts. (OCR and readings often send no score; they are never checked.)</p>
        )}
      </div>
    </div>
  );
}
